import { useState } from "react";
import axios from "axios";
import { motion, useReducedMotion } from "framer-motion";
import { Loader2, Mail, MapPin, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { COMPANY, SERVICE_OPTIONS } from "@/lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
    website: "", // honeypot
};

export default function Contact() {
    const reduce = useReducedMotion();
    const [form, setForm] = useState(initial);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const validate = () => {
        const e = {};
        if (!form.name || form.name.trim().length < 2)
            e.name = "Bitte Namen angeben.";
        if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
            e.email = "Bitte gültige E-Mail angeben.";
        if (!form.message || form.message.trim().length < 5)
            e.message = "Bitte beschreiben Sie kurz Ihr Anliegen.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onChange = (k) => (ev) => {
        const value = ev?.target ? ev.target.value : ev;
        setForm((prev) => ({ ...prev, [k]: value }));
        if (errors[k]) setErrors((p) => ({ ...p, [k]: undefined }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            toast.error("Bitte korrigieren Sie die markierten Felder.");
            return;
        }

        // Honeypot — silently no-op, mirrors backend anti-spam behavior
        if (form.website) {
            setForm(initial);
            return;
        }

        setLoading(true);

        // Best-effort lead storage — the mailto below is the actual delivery path,
        // so a failed/slow backend must never block opening the mail client.
        try {
            await axios.post(`${API}/contact`, form, {
                headers: { "Content-Type": "application/json" },
                timeout: 20000,
            });
        } catch (err) {
            // ignore
        }

        const subject = form.service
            ? `Anfrage von ${form.name} – ${form.service}`
            : `Anfrage von ${form.name}`;
        const bodyLines = [
            `Name: ${form.name}`,
            `E-Mail: ${form.email}`,
            form.phone && `Telefon: ${form.phone}`,
            form.service && `Leistung: ${form.service}`,
            "",
            form.message,
        ].filter(Boolean);
        const mailtoLink = `mailto:${COMPANY.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join("\n"))}`;

        toast.success("Ihr E-Mail-Programm öffnet sich gleich – bitte Nachricht dort absenden.");
        setForm(initial);
        setLoading(false);
        window.location.href = mailtoLink;
    };

    return (
        <section
            id="kontakt"
            data-testid="contact-section"
            className="relative py-20 sm:py-24 lg:py-28 border-t border-white/[0.06]"
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
                    <motion.div
                        initial={reduce ? false : { opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.5 }}
                    >
                        <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#7BD3FF]">
                            Kontakt
                        </p>
                        <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
                            Sprechen wir über Ihr <span className="text-chrome">Objekt</span>.
                        </h2>
                        <p className="mt-4 text-[#C9D3E1] text-base leading-relaxed max-w-lg">
                            Telefon ist am schnellsten – alternativ schreiben Sie uns hier. Wir antworten in der Regel innerhalb eines Werktags.
                        </p>

                        <div className="mt-8 space-y-3">
                            <a
                                href={`tel:${COMPANY.phoneTel}`}
                                className="flex items-center gap-3 rounded-xl border border-white/[0.10] bg-[rgba(18,27,42,0.55)] backdrop-blur-md p-4 hover:border-[rgba(123,211,255,0.35)] transition-colors"
                                data-testid="contact-call-link"
                            >
                                <span className="h-10 w-10 rounded-lg bg-[rgba(46,168,255,0.12)] border border-[rgba(46,168,255,0.25)] flex items-center justify-center">
                                    <Phone className="h-4 w-4 text-[#7BD3FF]" />
                                </span>
                                <span>
                                    <span className="block text-xs text-[#8FA0B8] font-mono">
                                        Telefon
                                    </span>
                                    <span className="text-[#F4F7FB] font-mono">
                                        {COMPANY.phone}
                                    </span>
                                </span>
                            </a>
                            <a
                                href={`mailto:${COMPANY.email}`}
                                className="flex items-center gap-3 rounded-xl border border-white/[0.10] bg-[rgba(18,27,42,0.55)] backdrop-blur-md p-4 hover:border-[rgba(123,211,255,0.35)] transition-colors"
                                data-testid="contact-email-link"
                            >
                                <span className="h-10 w-10 rounded-lg bg-[rgba(46,168,255,0.12)] border border-[rgba(46,168,255,0.25)] flex items-center justify-center">
                                    <Mail className="h-4 w-4 text-[#7BD3FF]" />
                                </span>
                                <span>
                                    <span className="block text-xs text-[#8FA0B8] font-mono">
                                        E-Mail
                                    </span>
                                    <span className="text-[#F4F7FB]">
                                        {COMPANY.email}
                                    </span>
                                </span>
                            </a>
                            <div className="flex items-center gap-3 rounded-xl border border-white/[0.10] bg-[rgba(18,27,42,0.55)] backdrop-blur-md p-4">
                                <span className="h-10 w-10 rounded-lg bg-[rgba(46,168,255,0.12)] border border-[rgba(46,168,255,0.25)] flex items-center justify-center">
                                    <MapPin className="h-4 w-4 text-[#7BD3FF]" />
                                </span>
                                <span>
                                    <span className="block text-xs text-[#8FA0B8] font-mono">
                                        Adresse
                                    </span>
                                    <span className="text-[#F4F7FB]">
                                        {COMPANY.address.street}, {COMPANY.address.city}
                                    </span>
                                </span>
                            </div>
                        </div>
                    </motion.div>

                    <motion.form
                        onSubmit={onSubmit}
                        initial={reduce ? false : { opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.55, delay: 0.05 }}
                        className="rounded-2xl border border-white/[0.10] bg-[rgba(14,20,31,0.75)] backdrop-blur-md p-6 sm:p-8 card-elevated"
                        data-testid="contact-form"
                        noValidate
                    >
                        {/* honeypot */}
                        <div
                            aria-hidden="true"
                            style={{ position: "absolute", left: "-9999px", height: 0, width: 0, overflow: "hidden" }}
                        >
                            <label>
                                Website (bitte leer lassen)
                                <input
                                    type="text"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    value={form.website}
                                    onChange={onChange("website")}
                                />
                            </label>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-2">
                                <Label htmlFor="name" className="text-[#C9D3E1] text-sm">
                                    Name *
                                </Label>
                                <Input
                                    id="name"
                                    value={form.name}
                                    onChange={onChange("name")}
                                    placeholder="Ihr Name"
                                    className="bg-[rgba(7,9,12,0.5)] border-white/[0.12] text-white placeholder:text-[#8FA0B8]"
                                    aria-invalid={!!errors.name}
                                    data-testid="contact-name-input"
                                />
                                {errors.name && (
                                    <p className="text-xs text-[#FF5A6A]">{errors.name}</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-2">
                                <Label htmlFor="email" className="text-[#C9D3E1] text-sm">
                                    E-Mail *
                                </Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={form.email}
                                    onChange={onChange("email")}
                                    placeholder="name@beispiel.at"
                                    className="bg-[rgba(7,9,12,0.5)] border-white/[0.12] text-white placeholder:text-[#8FA0B8]"
                                    aria-invalid={!!errors.email}
                                    data-testid="contact-email-input"
                                />
                                {errors.email && (
                                    <p className="text-xs text-[#FF5A6A]">{errors.email}</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-2">
                                <Label htmlFor="phone" className="text-[#C9D3E1] text-sm">
                                    Telefon
                                </Label>
                                <Input
                                    id="phone"
                                    value={form.phone}
                                    onChange={onChange("phone")}
                                    placeholder="+43 …"
                                    className="bg-[rgba(7,9,12,0.5)] border-white/[0.12] text-white placeholder:text-[#8FA0B8]"
                                    data-testid="contact-phone-input"
                                />
                            </div>
                            <div className="flex flex-col gap-2">
                                <Label htmlFor="service" className="text-[#C9D3E1] text-sm">
                                    Leistung
                                </Label>
                                <Select
                                    value={form.service}
                                    onValueChange={(v) => onChange("service")(v)}
                                >
                                    <SelectTrigger
                                        id="service"
                                        className="bg-[rgba(7,9,12,0.5)] border-white/[0.12] text-white data-[placeholder]:text-[#8FA0B8]"
                                        data-testid="contact-service-select"
                                    >
                                        <SelectValue placeholder="Bitte wählen" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0E141F] border-white/[0.10] text-white">
                                        {SERVICE_OPTIONS.map((opt) => (
                                            <SelectItem
                                                key={opt}
                                                value={opt}
                                                className="focus:bg-[rgba(46,168,255,0.12)] focus:text-white"
                                            >
                                                {opt}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="flex flex-col gap-2 mt-4">
                            <Label htmlFor="message" className="text-[#C9D3E1] text-sm">
                                Nachricht *
                            </Label>
                            <Textarea
                                id="message"
                                rows={5}
                                value={form.message}
                                onChange={onChange("message")}
                                placeholder="Beschreiben Sie kurz Ihr Anliegen, Objektgröße, gewünschten Zeitraum …"
                                className="bg-[rgba(7,9,12,0.5)] border-white/[0.12] text-white placeholder:text-[#8FA0B8] resize-none"
                                aria-invalid={!!errors.message}
                                data-testid="contact-message-textarea"
                            />
                            {errors.message && (
                                <p className="text-xs text-[#FF5A6A]">{errors.message}</p>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="mt-6 inline-flex w-full sm:w-auto items-center justify-center gap-2 px-6 h-12 rounded-xl bg-[#2EA8FF] text-[#07090C] font-medium hover:bg-[#7BD3FF] transition-colors disabled:opacity-70 disabled:cursor-not-allowed btn-glow"
                            data-testid="contact-submit-button"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Senden …
                                </>
                            ) : (
                                <>
                                    <Send className="h-4 w-4" />
                                    Nachricht senden
                                </>
                            )}
                        </button>

                        <p className="mt-4 text-xs text-[#8FA0B8]">
                            Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Daten gemäß unserer{" "}
                            <a
                                href="/datenschutz"
                                className="underline underline-offset-2 hover:text-white"
                            >
                                Datenschutzerklärung
                            </a>{" "}
                            zu.
                        </p>
                    </motion.form>
                </div>
            </div>
        </section>
    );
}
